import { AddCircleIcon, GroupsIcon } from '@/assets/icons'
import { countLabel } from '@/utils/plural'
import { Button } from '@components/Button'
import EmptyState, { EmptyStateButton } from '@components/EmptyState'
import { PageColumn, PageHeader, PageScroll } from '@components/Page'
import PageLoader from '@components/PageLoader'
import TripCard from '@components/TripCard'
import { Trip } from '@types'
import React from 'react'
import { TripsGrid } from './TripsList.styles'
import { TestIds } from '@/testIds'
import { iconSizes } from '@/theme/tokens'
import { ErrorBanner } from '@components/Notice'

interface TripsListProps {
	trips: Trip[]
	loading: boolean
	error: string | null
	onTripClick: (trip: Trip) => void
	onCreateTrip: () => void
	onJoinTrip: () => void
}

const TripsList: React.FC<TripsListProps> = ({
	trips,
	loading,
	error,
	onTripClick,
	onCreateTrip,
	onJoinTrip,
}) => {
	if (loading) {
		return (
			<PageScroll>
				<PageLoader label="Loading your trips…" />
			</PageScroll>
		)
	}

	if (error) {
		return (
			<PageScroll>
				<PageColumn>
					<ErrorBanner>
						<strong>Error loading trips:</strong> {error}
					</ErrorBanner>
				</PageColumn>
			</PageScroll>
		)
	}

	// Show empty state when there are no trips
	if (trips.length === 0) {
		return (
			<PageScroll>
				<EmptyState
					art="flights"
					title="No trips yet"
					description="Join a trip or create your own to get started."
					testId={TestIds.noTrip.screen}
				>
					<EmptyStateButton onClick={onCreateTrip} data-testid={TestIds.noTrip.addTripButton}>
						<AddCircleIcon sx={{ fontSize: iconSizes.lg }} />
						<span>Create Trip</span>
					</EmptyStateButton>
					<EmptyStateButton onClick={onJoinTrip}>
						<GroupsIcon sx={{ fontSize: iconSizes.lg }} />
						<span>Join Trip</span>
					</EmptyStateButton>
				</EmptyState>
			</PageScroll>
		)
	}

	return (
		<PageScroll>
			<PageColumn>
				<PageHeader
					title="My Trips"
					subtitle={countLabel(trips.length, 'trip')}
					actions={
						<>
							<Button onClick={onCreateTrip} data-testid={TestIds.trips.addButton}>
								<AddCircleIcon sx={{ fontSize: iconSizes.lg }} />
								<span>Create Trip</span>
							</Button>
							<Button onClick={onJoinTrip}>
								<GroupsIcon sx={{ fontSize: iconSizes.lg }} />
								<span>Join Trip</span>
							</Button>
						</>
					}
				/>

				<TripsGrid data-testid={TestIds.trips.list}>
					{trips.map(trip => (
						<TripCard key={trip.id} trip={trip} onClick={onTripClick} />
					))}
				</TripsGrid>
			</PageColumn>
		</PageScroll>
	)
}

export default TripsList
